import * as _ from 'underscore';

import { Recurrence } from '@tableau/self-service-schedules';

import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { appStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { L10n } from '../../ts/L10n/L10n';
import { IDatasourcePublishResult, ISyncPublishResult } from '../controllers/DatasourcePublishResultCtrl';
import { ILocLinkifierModel } from '../filters/LocLinkifierFilter';
import { DateHelpers } from '../helpers/DateHelpers';
import { RefreshPropertyHelper } from '../helpers/RefreshPropertyHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { IDataSourceBase, IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { Module } from '../Module';
import { RefreshOptionType } from '../resources/Datasources';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { RemoteRefreshSchedules } from '../resources/RemoteRefreshSchedules';
import { IGetRemoteRefreshAgentsResult } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { IResult } from '../ServerApi/types/IResponse';
import { ChangeDatasourceRefreshModeAction } from '../services/ChangeDatasourceRefreshModeAction';
import { ChangeDatasourceRemoteAgentAction } from '../services/ChangeDatasourceRemoteAgentAction';
import { ChangeDatasourceRemoteAgentReservationAction } from '../services/ChangeDatasourceRemoteAgentReservationAction';
import {
  ChangeDatasourceRemoteRefreshPropertiesAction,
  IChangeDatasourceRemoteRefreshPropertiesScope,
  IRemoteRefreshAgentOption,
  IRemoteRefreshPropertiesEditModel,
} from '../services/ChangeDatasourceRemoteRefreshPropertiesAction';
import { CreateExtractTasksAction, CreateExtractTasksMode } from '../services/CreateExtractTasksAction';
import { ICreateRemoteRefreshSelfServiceScheduleScope, setupDefaultScheduleDetailsForSSSWidget } from '../services/CreateRemoteRefreshScheduleAction';
import { DesktopApplicationLaunchService } from '../services/DesktopApplicationLaunchService';
import { IModalScope, ModalService, ModalType } from '../services/ModalService';
import { ServerService } from '../services/ServerService';
import { ChangeScheduleAction } from './ChangeScheduleAction';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { HelpService } from './HelpService';

interface IDatasourcePublishSuccessDialogScope extends IModalScope {
  dataSourceDetail: IDataSourceDetailInfo;
  datasourceSuccessMessage: string;
  postPublishStep: PostPublishStep;
}

export interface IDatasourcePublishRemoteRefreshSelfServiceScheduleDialogScope extends ICreateRemoteRefreshSelfServiceScheduleScope, IChangeDatasourceRemoteRefreshPropertiesScope {
  dataSourceDetail: IDataSourceDetailInfo;
  addAgentLink: () => ILocLinkifierModel;
  newAgentOption: IRemoteRefreshAgentOption;
}

enum PostPublishStep {
  Done,
  ServerRefresh,
  OnlineSyncRefresh,
  OnlineLiveQuerySync
}


export class PublishDatasourceAction {

  //// A special agent for the option to add a new agent.
  // "New agent" option that launches a Sync Client protocol handler when selected
  private newAgentLaunchOption: IRemoteRefreshAgentOption;
  // "New agent" option that does not launch Sync Client, used for reservations
  private newAgentReservationOption: IRemoteRefreshAgentOption;

  public static $inject: any = [
    '$q',
    '$translate',
    '$rootScope',
    '$window',
    'ModalService',
    'RemoteRefreshSchedules',
    'ChangeDatasourceRefreshModeAction',
    'ChangeDatasourceRemoteAgentAction',
    'ChangeDatasourceRemoteAgentReservationAction',
    'ChangeScheduleAction',
    'DesktopApplicationLaunchService',
    'ChangeDatasourceRemoteRefreshPropertiesAction',
    'ServerService',
    'CreateExtractTasksAction',
    'HelpService',
    'RemoteRefreshAgents',
    'ConfirmActionDialog'
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private $rootScope: ng.IRootScopeService,
    private $window: ng.IWindowService,
    private ModalService: ModalService,
    private RemoteRefreshSchedules: RemoteRefreshSchedules,
    private ChangeDatasourceRefreshModeAction: ChangeDatasourceRefreshModeAction,
    private ChangeDatasourceRemoteAgentAction: ChangeDatasourceRemoteAgentAction,
    private ChangeDatasourceRemoteAgentReservationAction: ChangeDatasourceRemoteAgentReservationAction,
    private ChangeScheduleAction: ChangeScheduleAction,
    private DesktopApplicationLaunchService: DesktopApplicationLaunchService,
    private ChangeDatasourceRemoteRefreshPropertiesAction: ChangeDatasourceRemoteRefreshPropertiesAction,
    private ServerService: ServerService,
    private CreateExtractTasksAction: CreateExtractTasksAction,
    private HelpService: HelpService,
    private RemoteRefreshAgents: RemoteRefreshAgents,
    private ConfirmActionDialog: ConfirmActionDialog
  ) {

    this.newAgentReservationOption = {
      display: $translate.instant('remoteRefreshAgentSetupLocal'),
      // "name" is unique for remote agents. Use a unique special value for testing equality with this option
      name: '@@reserve-local-agent@@'
    };

    this.newAgentLaunchOption = {
      display: $translate.instant('remoteRefreshAgentSetupLocal'),
      // "name" is unique for remote agents. Use a unique special value for testing equality with this option.
      name: '@@launch-local-agent@@',
      launchesSyncClient: true
    };
  }

  public getNewAgentReservationOption() {
    return this.newAgentReservationOption;
  }

  public handlePublishedDatasource(countOfSchedules: number, publishResult: IDatasourcePublishResult, datasource: IDataSourceDetailInfo) {
    if (publishResult && publishResult.preferredRefreshOption) {
      // Make our best attempt to fire this off, even when the window closes.
      const unloadListener = () =>
        this.RemoteRefreshAgents.completeWorkflowDuringWindowUnload(publishResult.remoteAgentWorkflowId);
      this.$window.addEventListener('unload', unloadListener, false);

      let handlingResult: ng.IPromise<any> = this.$q.when();

      if (countOfSchedules === 0) {
        if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.LiveConnection) {
          // If it is a live connection check if it was published with live query sync

          // we need to let angular finish up some stuff so defer
          _.defer(() => {
            if (publishResult.publishedWithLQS){
              // reach here if url is: datasources/id/publishresult?publishedWithLQS=y
              handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.OnlineLiveQuerySync);
              // $MARK bmoulin TFSID:581383:As a User I can chose which LQA agent from post-publish success dialog
            } else {
              handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.Done);
            }
          });

        } else if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.Server) {
          // If it is a server-refresh
          handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.ServerRefresh)
            .then(() => this.ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, null, true, false))
            .then(() => this.CreateExtractTasksAction.forDatasource(datasource, {
              scope: this.$rootScope,
              targetElement: null
            }, CreateExtractTasksMode.AllowSchedule))
            .then(() => appStore.dispatch(incrementRouterSpaceReactKey()));

        } else if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.OnlineSync) {
          // If it is a remote refresh
          handlingResult = this.RemoteRefreshAgents.get().then((remoteAgents: IGetRemoteRefreshAgentsResult) => {
            if (!_.isEmpty(remoteAgents.agents) || this.canLaunchSync(publishResult) || this.canReserveSyncWithWorkflow(publishResult)) {
              // Continue to scheduling a Sync Client if that is an option
              return this.displaySuccessDialog(datasource, PostPublishStep.OnlineSyncRefresh)
                .then(() => this.displayRemoteRefreshScheduleDialog(publishResult, datasource))
                .then(() => appStore.dispatch(incrementRouterSpaceReactKey()));

            } else {
              return this.displaySuccessDialog(datasource, PostPublishStep.Done);
            }
          });
        }

        handlingResult.finally(() => {
          // Clear this from the scope so the dialog won't appear again if the user navigates back to this state
          if (publishResult) {
            publishResult.preferredRefreshOption = null;
          }
        });
      }

      handlingResult.finally(() => {
        this.$window.removeEventListener('unload', unloadListener, false);
        this.RemoteRefreshAgents.completeWorkflow(publishResult.remoteAgentWorkflowId);
      });
    }
  }

  private displaySuccessDialog(datasource: IDataSourceDetailInfo, postPublishStep: PostPublishStep): ng.IPromise<any> {
    const successDialogScope: IDatasourcePublishSuccessDialogScope = <IDatasourcePublishSuccessDialogScope>this.$rootScope.$new();

    successDialogScope.datasourceSuccessMessage = Messages.publishDatasourceSuccessDialogTextPublished({ DATASOURCE_NAME: L10n.elideIfLong(datasource.name) });
    successDialogScope.postPublishStep = postPublishStep;

    const modalInstance = this.ModalService.open({
      size: '500px',
      type: ModalType.Unification,
      title: Messages.publishDatasourceSuccessDialogTitleComplete(),
      templateUrl: 'datasourcePublishSuccessDialog.html',
      scope: successDialogScope,
      controller: [
        '$scope', (dialogScope: IDatasourcePublishSuccessDialogScope) => {
          dialogScope.dataSourceDetail = datasource;
        }
      ]
    });

    return modalInstance.result.finally(() => successDialogScope.$destroy());
  }

  private getDefaultAgentName(agents: IRemoteRefreshAgentOption[]): string {
    if (agents.length === 0) return '';

    const recentSyncThreshold = 10*60*1000; // ten minutes, in milliseconds
    const nowTimestamp = Date.now();

    const mostRecentAgent: IRemoteRefreshAgentOption = _.max(agents, (agent) => agent.schedulesSyncedAt ? DateHelpers.fromISOString(agent.schedulesSyncedAt).getTime() : 0);

    // If the most recent agent sync'd within the recency threshold, return it, otherwise return the first in the list.
    if (mostRecentAgent.schedulesSyncedAt && (nowTimestamp - DateHelpers.fromISOString(mostRecentAgent.schedulesSyncedAt).getTime() < recentSyncThreshold)) {
      return mostRecentAgent.name;
    } else {
      return agents[0].name;
    }
  }

  public remoteRefreshSelfServiceScheduleDialogScopeSetup(publishResult: ISyncPublishResult, scheduleDialogScope: IDatasourcePublishRemoteRefreshSelfServiceScheduleDialogScope, allowLaunchSync: boolean) {
    const datasource = scheduleDialogScope.dataSourceDetail;

    scheduleDialogScope.editModel = { refreshMode: datasource.refreshMode, remoteAgentName: datasource.remoteAgentName };

    if (this.canReserveSyncWithWorkflow(publishResult) && _.isEmpty(scheduleDialogScope.remoteAgentOptions)) {
      // Add the option for adding a new remote agent ("My Computer") that saves a Sync Client reservation
      scheduleDialogScope.remoteAgentOptions = [this.newAgentReservationOption];
      scheduleDialogScope.editModel.remoteAgentName = this.newAgentReservationOption.name;

    } else if (allowLaunchSync && this.canLaunchSync(publishResult)) {
      // Add the option for adding a new remote agent ("My Computer") that launches Sync Client, and
      // make it the default option
      scheduleDialogScope.remoteAgentOptions.unshift(this.newAgentLaunchOption);
      scheduleDialogScope.editModel.remoteAgentName = this.newAgentLaunchOption.name;

    } else if (scheduleDialogScope.remoteAgentOptions) {
      // Otherwise the first remote agent should be selected unless one last sync'd very recently.
      scheduleDialogScope.editModel.remoteAgentName = this.getDefaultAgentName(scheduleDialogScope.remoteAgentOptions);
    }

    // Set up schedule
    setupDefaultScheduleDetailsForSSSWidget(scheduleDialogScope, RefreshPropertyHelper.getAvailableRefreshTypes(scheduleDialogScope.dataSourceDetail), this.ChangeScheduleAction, null);

    scheduleDialogScope.addAgentLink = () => {
      return {
        links: [{
          localizedValueKey: 'remoteRefreshAgentLearnHow',
          url: this.HelpService.getServerHelpUrlByCategory('bridge-add-client')
        }]
      };
    };

    scheduleDialogScope.$watch('editModel.remoteAgentName', (newValue: string, oldValue: string, scope: IDatasourcePublishRemoteRefreshSelfServiceScheduleDialogScope) => {
      const agent = _.find(scope.remoteAgentOptions, r => r.name === newValue);
      if (agent) {
        const timeZoneDetails = scope.selfServiceSchedulePaneProps.timeZoneDetails;
        timeZoneDetails.timeZonesEnabled = true;
        timeZoneDetails.timeZoneDisplayName = agent.remoteAgentTimeZone;
        scope.selfServiceSchedulePaneProps = {
          recurrenceModel: scope.selfServiceSchedulePaneProps.recurrenceModel,
          timeZoneDetails: timeZoneDetails,
          recurrenceModelChangedCallback: scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback,
          type: scope.selfServiceSchedulePaneProps.type,
        };
      }
    });
  }

  public createRemoteRefreshSelfServiceSchedule(
    datasource: IDataSourceBase,
    refreshType: string,
    recurrence: Recurrence,
    editModel: IRemoteRefreshPropertiesEditModel,
    publishResult: ISyncPublishResult,
    suppressSuccessToast: boolean = false
  ): ng.IPromise<IResult> {
    // Create the remote refresh schedule
    const scheduleInfo: IRemoteRefreshScheduleInfo = {
      id: '',
      contentType: 'remoteRefreshSchedule',
      definition: '',
      refreshType: refreshType,
      scheduleDetails: recurrence.recurrenceDetails,
      scheduleType: recurrence.recurrenceType
    };

    return this.createRefreshSchedule(datasource, scheduleInfo, editModel, publishResult, suppressSuccessToast);
  }

  private createRefreshSchedule(
    datasource: IDataSourceBase,
    scheduleInfo: IRemoteRefreshScheduleInfo,
    editModel: IRemoteRefreshPropertiesEditModel,
    publishResult: ISyncPublishResult,
    suppressSuccessToast: boolean
  ): ng.IPromise<IResult> {
    return this.RemoteRefreshSchedules.create(datasource.id, scheduleInfo)
      .then(ResultHelper.rejectResultOnError(this.$q))
      // Then change the refresh mode to 'remote agent'
      .then(() => this.ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Remote, editModel.remoteAgentName, true, false))
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then(() => {

        if (editModel.remoteAgentName === this.newAgentLaunchOption.name) {
          // If we're adding a new remote agent, create new sync client and assign to this datasource
          return this.DesktopApplicationLaunchService.launchSyncClientAndAssignToDatasource(
            this.ServerService.sessionSiteUrlName(),
            datasource.urlName);
        } else if (editModel.remoteAgentName === this.newAgentReservationOption.name) {
          return this.ChangeDatasourceRemoteAgentReservationAction
            .execute(datasource, publishResult.remoteAgentWorkflowId, suppressSuccessToast);
        } else {
          // Otherwise, switch datasource to the selected remote agent
          return this.ChangeDatasourceRemoteAgentAction.execute(datasource,
            editModel.remoteAgentName, RefreshModeTypes.Remote, suppressSuccessToast,
            publishResult.remoteAgentWorkflowId);
        }
      })
      .then(ResultHelper.rejectResultOnError(this.$q));
  }

  public displayRemoteRefreshScheduleDialog(publishResult: ISyncPublishResult, datasource: IDataSourceDetailInfo): ng.IPromise<IResult> {
    let scheduleDialogScope: IDatasourcePublishRemoteRefreshSelfServiceScheduleDialogScope = <IDatasourcePublishRemoteRefreshSelfServiceScheduleDialogScope>this.$rootScope.$new();
    scheduleDialogScope.dataSourceDetail = datasource;
    const setupScope = (scope: IDatasourcePublishRemoteRefreshSelfServiceScheduleDialogScope) => {
      scheduleDialogScope = scope;
      scheduleDialogScope.dataSourceDetail = datasource;
      this.remoteRefreshSelfServiceScheduleDialogScopeSetup(publishResult, scheduleDialogScope, true);

      return this.ConfirmActionDialog.open({
        title: this.$translate.instant('publishDatasourceRemoteRefreshDialogTitle'),
        bodyTemplateUrl: 'datasourcePublishRemoteRefreshSelfServiceScheduleDialog.html',
        scope: scheduleDialogScope,
        size: '500px',
        action: angular.noop,
        actionName: this.$translate.instant('publishDatasourceSuccessDialogScheduleBtn'),
        position: {
          reserveHeight: 400
        },
        confirmDisabled: () => false
      }).result;
    };
    const createRefreshSchedule = () => this.createRemoteRefreshSelfServiceSchedule(scheduleDialogScope.dataSourceDetail, scheduleDialogScope.scheduleInfo.refreshType,
      scheduleDialogScope.selfServiceSchedulePaneProps.recurrenceModel, scheduleDialogScope.editModel, publishResult);

    return this.ChangeDatasourceRemoteRefreshPropertiesAction.populateAvailableRemoteAgentOptions(datasource, scheduleDialogScope)
      .then(setupScope)
      .then(createRefreshSchedule)
      .finally(() => scheduleDialogScope.$destroy());
  }

  private canLaunchSync(publishResult: ISyncPublishResult) {
    return UserAgentSupport.isWindows()
      && publishResult.isSyncAvailable
      && !publishResult.isSyncRunning
      // Instead of launching Sync Client from the browser,
      // a workflow ID implies that Desktop already launched the Sync Client.
      && _.isEmpty(publishResult.remoteAgentWorkflowId);
  }

  public canReserveSyncWithWorkflow(publishResult: ISyncPublishResult) {
    return !_.isEmpty(publishResult.remoteAgentWorkflowId);
  }
}

Module.instance().service('PublishDatasourceAction', PublishDatasourceAction);
